"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { getNextMintStart } from "@/components/meme-calendar/meme-calendar.helpers";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ImageScale } from "@/helpers/image.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import Link from "next/link";
interface NextMintCardProps {
  readonly drop: ApiDrop;
}
const formatNextMintDateTime = (date: Date): string => {
  const dateParts = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short"
  }).formatToParts(date);
  const day = dateParts.find(p => p.type === "day")?.value ?? "";
  const month = dateParts.find(p => p.type === "month")?.value ?? "";
  const dateLabel = `${day} ${month}`;
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
  return `${dateLabel} · ${timeLabel}`;
};
export const NextMintCard = t0 => {
  const $ = _c(29);
  const {
    drop
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const nextMintDate = getNextMintStart();
    t1 = formatNextMintDateTime(nextMintDate);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const timestamp = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/5 tw-bg-[#111111] tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2"><div className="tw-flex tw-items-center tw-gap-2"><span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" /><span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide tw-text-emerald-400">NEXT MINT</span></div><span className="tw-font-mono tw-text-xs tw-text-white/50">{timestamp}</span></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== hasTouchScreen || $[3] !== media) {
    t3 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-black/50"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{media ? <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageObjectPosition="center" imageScale={ImageScale.AUTOx600} disableAutoPlay={hasTouchScreen} disableModal={hasTouchScreen} /> : <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-black/40"><span className="tw-text-sm tw-text-white/40">No image</span></div>}</div></div>;
    $[2] = hasTouchScreen;
    $[3] = media;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = media?.mime_type;
  let t5;
  if ($[5] !== drop.id || $[6] !== t4) {
    t5 = <MediaTypeBadge mimeType={t4} dropId={drop.id} size="sm" />;
    $[5] = drop.id;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = `/waves?wave=${drop.wave.id}&drop=${drop.id}`;
  let t7;
  if ($[8] !== t6 || $[9] !== title) {
    t7 = <Link href={t6} className="tw-m-0 tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-text-base tw-font-semibold tw-leading-tight tw-text-white tw-no-underline tw-transition-colors group-hover:tw-text-white/80 @lg:tw-line-clamp-1">{title}</Link>;
    $[8] = t6;
    $[9] = title;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">{t5}{t7}</div>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = `/${author.handle ?? author.primary_address}`;
  const t10 = author.handle ?? "Artist";
  let t11;
  if ($[14] !== author.pfp || $[15] !== t10) {
    t11 = <ProfileAvatar pfpUrl={author.pfp} alt={t10} size={ProfileBadgeSize.SMALL} />;
    $[14] = author.pfp;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = author.handle ?? "Anonymous";
  let t13;
  if ($[17] !== t12) {
    t13 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t12}</span>;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t11 || $[20] !== t13 || $[21] !== t9) {
    t14 = <Link href={t9} className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t11}{t13}</Link>;
    $[19] = t11;
    $[20] = t13;
    $[21] = t9;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14 || $[24] !== t8) {
    t15 = <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-white/5 tw-px-3 tw-py-3 sm:tw-px-4">{t8}{t14}</div>;
    $[23] = t14;
    $[24] = t8;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15 || $[27] !== t3) {
    t16 = <div className="tw-group tw-flex tw-flex-col tw-text-left tw-transition-all tw-duration-300"><div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/5 tw-bg-[#0c0c0c] tw-transition-colors group-hover:tw-border-white/10">{t2}{t3}{t15}</div></div>;
    $[26] = t15;
    $[27] = t3;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
};
function _temp(m) {
  return m.data_key === "title";
}